import { get, getWithTimestamp, remove, set } from 'lib/storage/versionedLocalStorageUtils'
import type { BusinessTravellerAccountSetupState } from 'businessTraveller/contexts/BusinessTravellerAccountSetupContexts'
import { logDatadog } from 'services/datadogRum'

// Redux state
// Increment version when we change the state structure in an incompatible way.
const REDUX_SELECTED_TRAVELLER_STATE_KEY = 'BusinessTravellerSelectedTraveller'
const REDUX_SELECTED_TRAVELLER_STATE_VERSION = 2

type BusinessTravellerSelectedTravellerState = {
  employeeIds?: Array<string>
  guestCount?: number
} | undefined

export function saveBusinessTravellerSelectedTravellerState(employeeIds: Array<string>, guestCount?: number) {
  set(REDUX_SELECTED_TRAVELLER_STATE_KEY, REDUX_SELECTED_TRAVELLER_STATE_VERSION, { employeeIds, guestCount: guestCount ?? 0 })
}

export function getBusinessTravellerSelectedTravellerState(): BusinessTravellerSelectedTravellerState {
  return get(REDUX_SELECTED_TRAVELLER_STATE_KEY, REDUX_SELECTED_TRAVELLER_STATE_VERSION)
}

export function clearBusinessTravellerSelectedTraveller() {
  remove(REDUX_SELECTED_TRAVELLER_STATE_KEY)
}

const PERSONAL_ACCOUNT_LINKING_DISMISSED_KEY = 'BusinessTravellerPersonalAccountLinkingDismissed'
const PERSONAL_ACCOUNT_LINKING_DISMISSED_VERSION = 1
const PERSONAL_ACCOUNT_LINKING_DISMISSED_TTL_MS = 60 * 60 * 1000

// The employee who closed the login linking prompt in the last hour; cleared on sign-in and sign-out.
export function getPersonalAccountLinkingDismissedEmployeeId(): string | null {
  const { value, timestamp } = getWithTimestamp<string>(PERSONAL_ACCOUNT_LINKING_DISMISSED_KEY, PERSONAL_ACCOUNT_LINKING_DISMISSED_VERSION)
  return value && timestamp && Date.now() < timestamp + PERSONAL_ACCOUNT_LINKING_DISMISSED_TTL_MS ? value : null
}

export function setPersonalAccountLinkingDismissedEmployeeId(employeeId: string) {
  set(PERSONAL_ACCOUNT_LINKING_DISMISSED_KEY, PERSONAL_ACCOUNT_LINKING_DISMISSED_VERSION, employeeId)
}

export function clearPersonalAccountLinkingDismissedEmployeeId() {
  remove(PERSONAL_ACCOUNT_LINKING_DISMISSED_KEY)
}

// Mapping trip request traveller details to the traveller form on checkout page.
// Used to pre-fill traveller forms with details from the trip request during cart restoration
const TRIP_REQUEST_TRAVELLER_FORM_MAPPING_KEY = 'BusinessTravellerTripRequestTravellerFormMapping'
const TRIP_REQUEST_TRAVELLER_FORM_MAPPING_VERSION = 1

type TripRequestTravellerFormMappingState = {
  travellerFormMappings: Array<App.BusinessTraveller.TripRequestTravellerFormMapping>
} | undefined

export function saveBusinessTravellerTripRequestTravellerFormMappings(travellerFormMappings: Array<App.BusinessTraveller.TripRequestTravellerFormMapping>) {
  set(TRIP_REQUEST_TRAVELLER_FORM_MAPPING_KEY, TRIP_REQUEST_TRAVELLER_FORM_MAPPING_VERSION, { travellerFormMappings })
}

export function getBusinessTravellerTripRequestTravellerFormMappings(): TripRequestTravellerFormMappingState {
  return get(TRIP_REQUEST_TRAVELLER_FORM_MAPPING_KEY, TRIP_REQUEST_TRAVELLER_FORM_MAPPING_VERSION)
}

export function clearBusinessTravellerTripRequestTravellerFormMappings() {
  remove(TRIP_REQUEST_TRAVELLER_FORM_MAPPING_KEY)
}

// Employee setup flow – sessionStorage so refresh keeps step; cleared when flow completes
const EMPLOYEE_SETUP_STORAGE_PREFIX = 'BusinessTravellerEmployeeSetup'
export const EMPLOYEE_SETUP_LOG_PREFIX = '[BusinessTravellerEmployeeSetup]'

function getEmployeeSetupStorageKey(invitationId: string, employeeEmail: string) {
  return `${EMPLOYEE_SETUP_STORAGE_PREFIX}_${invitationId}_${encodeURIComponent(employeeEmail)}`
}

export function getEmployeeSetupState(invitationId: string, employeeEmail: string): Partial<BusinessTravellerAccountSetupState> | null {
  try {
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(getEmployeeSetupStorageKey(invitationId, employeeEmail)) : null
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<BusinessTravellerAccountSetupState>
    return parsed && typeof parsed.step === 'number' ? parsed : null
  } catch(err) {
    logDatadog(`${EMPLOYEE_SETUP_LOG_PREFIX} getEmployeeSetupState failed`, { err }, 'warn')
    return null
  }
}

export function setEmployeeSetupState(invitationId: string, employeeEmail: string, state: BusinessTravellerAccountSetupState) {
  try {
    if (typeof sessionStorage === 'undefined') return
    // Never persist the password — it's kept in memory only; a refresh re-collects it.
    const { password: _password, ...persistableState } = state
    sessionStorage.setItem(getEmployeeSetupStorageKey(invitationId, employeeEmail), JSON.stringify(persistableState))
  } catch(err) {
    logDatadog(`${EMPLOYEE_SETUP_LOG_PREFIX} setEmployeeSetupState failed`, { err }, 'warn')
  }
}

export function clearEmployeeSetupState(invitationId: string, employeeEmail: string) {
  try {
    if (typeof sessionStorage === 'undefined') return
    sessionStorage.removeItem(getEmployeeSetupStorageKey(invitationId, employeeEmail))
  } catch(err) {
    logDatadog(`${EMPLOYEE_SETUP_LOG_PREFIX} clearEmployeeSetupState failed`, { err }, 'warn')
  }
}
